import { HTMLAttributes } from 'react';
import { ControllerRenderProps, FieldError } from 'react-hook-form';

import { Dictionary } from '../../../primitives/dictionary';
import { InputNumber } from '../../../primitives/input-number';
import { InputPhone } from '../../../primitives/input-phone';
import { CalendarDatePickerV2, MEXICO_TIMEZONE } from '../../calendar/calendar-date-picker-v2';
import { CodeEditor } from '../../code-editor';
import { ComboboxIcon } from '../../combobox/combobox-icon';
import { EditorPlain } from '../../editor/components/editor-plain';
import { ExtensionsProps } from '../../editor/extensions';
import { InputBucket } from '../../input/input-bucket';
import { InputField } from '../../input/input-field';
import { InputId } from '../../input/input-id';
import { InputMedia } from '../../input/input-media';
import {
  ScheduleAction,
  ScheduleActionItemErrors,
  ScheduleActionValue,
} from '../../schedule/schedule-action';
import { ScheduleStartEnd } from '../../schedule/schedule-start-end';
import { SwitchCard } from '../../switch/switch-card';
import { TextareaField } from '../../textarea/textarea-field';
import { CONTROL } from '../constants/control';
import { Attribute } from './attribute';
import { CheckboxRender } from './checkbox-render';
import { FieldWrapper } from './field-wrapper';
import { MultiSelectorRender } from './multi-selector-render';
import { RadioRender } from './radio-render';
import { SelectRender } from './select-render';

interface FormRenderProps extends Pick<HTMLAttributes<HTMLDivElement>, 'className'> {
  attribute: Attribute;
  error?: FieldError;
  ext?: ExtensionsProps;
  field: ControllerRenderProps<any>;
}

type NestedScheduleActionError = {
  actions?: Array<{
    audienceReference?: { contentId?: FieldError; moduleId?: FieldError };
    templateReference?: { contentId?: FieldError; moduleId?: FieldError };
  }>;
};

function getScheduleActionErrors(error?: FieldError): ScheduleActionItemErrors[] | undefined {
  const actions = (error as (FieldError & NestedScheduleActionError) | undefined)?.actions;

  return actions?.map((action) => ({
    audienceReference: {
      contentId: Boolean(action?.audienceReference?.contentId),
      moduleId: Boolean(action?.audienceReference?.moduleId),
    },
    templateReference: {
      contentId: Boolean(action?.templateReference?.contentId),
      moduleId: Boolean(action?.templateReference?.moduleId),
    },
  }));
}

export function FormRender({ attribute, className, error, ext, field }: FormRenderProps) {
  const { controlType, element, isLoading } = attribute;

  if (controlType === CONTROL.textArea) {
    const { id, button, counter, disabled, placeholder, size } = attribute;
    const { onChange, value } = field;

    if (element?.richText) {
      return (
        <FieldWrapper className={className} {...attribute}>
          <EditorPlain
            button={button}
            counter={counter}
            disabled={disabled}
            ext={ext}
            id={id}
            isLoading={isLoading}
            type="textarea"
            value={value}
            onChange={onChange}
          />
        </FieldWrapper>
      );
    }

    return (
      <FieldWrapper className={className} {...attribute}>
        <TextareaField
          button={button}
          counter={counter}
          id={id}
          disabled={disabled}
          isLoading={isLoading}
          placeholder={placeholder}
          size={size}
          {...field}
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.json || controlType === CONTROL.code) {
    const { id, disabled, theme, code, isLoading } = attribute;
    const { onChange, value } = field;

    return (
      <FieldWrapper className={className} {...attribute}>
        <CodeEditor
          disable={disabled}
          id={id}
          isLoading={isLoading}
          language={code?.language}
          languageLabel={code?.languageLabel}
          theme={theme === 'dark' ? 'vs-dark' : 'light'}
          value={value}
          onChange={onChange}
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.media) {
    const { id, button, disabled, onClick } = attribute;
    const { disabled: _disabled, ...restField } = field;
    return (
      <FieldWrapper className={className} {...attribute}>
        <InputMedia
          button={button}
          className="cursor-pointer"
          disabled={disabled}
          id={id}
          isLoading={isLoading}
          onClick={onClick ? onClick : () => {}}
          placeholder={attribute?.placeholder}
          readOnly
          {...restField}
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.notificationCenterTemplate) {
    const { id, button, disabled, onClick } = attribute;
    const { disabled: _disabled, ...restField } = field;
    return (
      <FieldWrapper className={className} {...attribute}>
        <InputBucket
          button={button}
          className="cursor-pointer"
          disabled={disabled}
          id={id}
          isLoading={isLoading}
          placeholder={attribute?.placeholder}
          readOnly
          onClick={onClick ? onClick : () => {}}
          {...restField}
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.dateTime) {
    const { button, id, disabled } = attribute;
    const { onChange, value } = field;
    const date = typeof value === 'number' ? new Date(value) : undefined;
    return (
      <FieldWrapper className={className} {...attribute}>
        <CalendarDatePickerV2
          button={button}
          date={date}
          disabled={disabled}
          id={id}
          isLoading={isLoading}
          mode="single"
          timeZone={MEXICO_TIMEZONE}
          yearsRange={200}
          onChange={(date) => {
            const selectedDate = date?.setHours(0, 0, 0, 0);
            selectedDate && onChange(selectedDate);
          }}
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.switch) {
    const { description, id, disabled, switcher, size, ...rest } = attribute;

    const rawValue = typeof field.value === 'boolean' ? field.value : false;
    const isInverted = switcher?.isInverted ?? false;

    const applyInversion = (value: boolean) => (isInverted ? !value : value);
    const displayValue = applyInversion(rawValue);

    return (
      <FieldWrapper id={id} className={className} {...rest}>
        <SwitchCard
          checked={displayValue}
          description={switcher?.description ?? description}
          disabled={disabled}
          id={id}
          isLoading={isLoading}
          label={
            displayValue ? (switcher?.onLabel ?? 'Encendido') : (switcher?.offLabel ?? 'Apagado')
          }
          sizesx={size}
          onCheckedChange={(checked) => field.onChange(applyInversion(checked))}
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.number) {
    const { id, disabled, max, min, placeholder } = attribute;
    const { onChange, value } = field;
    const numericValue = typeof value === 'number' ? value : '';
    return (
      <FieldWrapper className={className} {...attribute}>
        <InputNumber
          disabled={disabled}
          decimalScale={2}
          id={id}
          isLoading={isLoading}
          max={max}
          min={min}
          placeholder={placeholder}
          value={numericValue}
          onValueChange={(val) => {
            onChange(typeof val === 'number' ? val : undefined);
          }}
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.telephone) {
    const { disabled: _disabled, ...restField } = field;
    const { id, disabled, placeholder } = attribute;
    return (
      <FieldWrapper className={className} {...attribute}>
        <InputPhone
          disabled={disabled}
          id={id}
          isLoading={isLoading}
          placeholder={placeholder}
          {...restField}
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.multiSelector) {
    return (
      <MultiSelectorRender
        attribute={attribute}
        className={className}
        field={field}
        isLoading={isLoading}
      />
    );
  }

  if (controlType === CONTROL.radio) {
    return (
      <RadioRender
        attribute={attribute}
        className={className}
        field={field}
        isLoading={isLoading}
      />
    );
  }

  if (controlType === CONTROL.checkbox) {
    return (
      <CheckboxRender
        attribute={attribute}
        className={className}
        field={field}
        isLoading={isLoading}
      />
    );
  }

  if (controlType === CONTROL.select) {
    return (
      <SelectRender
        attribute={attribute}
        className={className}
        field={field}
        isLoading={isLoading}
      />
    );
  }

  if (controlType === CONTROL.email) {
    const { disabled: _disabled, ...restField } = field;
    const { id, button, disabled, counter, placeholder } = attribute;
    return (
      <FieldWrapper className={className} {...attribute}>
        <InputField
          {...restField}
          autoComplete="off"
          button={button}
          counter={counter}
          disabled={disabled}
          id={id}
          isLoading={isLoading}
          placeholder={placeholder}
          type="email"
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.url) {
    const { disabled: _disabled, ...restField } = field;
    const { id, button, counter, disabled, placeholder } = attribute;
    return (
      <FieldWrapper className={className} {...attribute}>
        <InputField
          {...restField}
          autoComplete="off"
          button={button}
          counter={counter}
          disabled={disabled}
          id={id}
          isLoading={isLoading}
          placeholder={placeholder}
          type="url"
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.icon) {
    const { id, button, disabled, placeholder } = attribute;
    const { onChange, value } = field;
    return (
      <FieldWrapper className={className} {...attribute}>
        <ComboboxIcon
          button={button}
          disabled={disabled}
          id={id}
          isLoading={isLoading}
          placeholder={placeholder}
          value={value}
          onSelect={(val) => onChange(val)}
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.dictionary) {
    const { id, disabled } = attribute;
    const { onChange, value } = field;
    return (
      <FieldWrapper className={className} {...attribute}>
        <Dictionary
          disabled={disabled}
          id={id}
          isLoading={isLoading}
          value={value}
          onChange={(value) => onChange(value)}
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.schedule) {
    const { id, isLoading, disabled } = attribute;
    const { onChange, value } = field;
    const scheduleRange =
      value && typeof value === 'object' && !Array.isArray(value) ? value : undefined;
    return (
      <FieldWrapper className={className} {...attribute}>
        <ScheduleStartEnd
          disabled={disabled}
          id={id}
          isLoading={isLoading}
          range={scheduleRange}
          onChange={(range) => onChange(range ?? null)}
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.actionSchedule && attribute.actionSchedule) {
    const { id, disabled } = attribute;
    const { onChange, value } = field;
    const scheduleActionValue =
      value && typeof value === 'object' && !Array.isArray(value)
        ? (value as ScheduleActionValue)
        : undefined;

    return (
      <FieldWrapper className={className} {...attribute}>
        <ScheduleAction
          {...attribute.actionSchedule}
          disabled={disabled}
          errors={getScheduleActionErrors(error)}
          id={id}
          value={scheduleActionValue}
          onChange={onChange}
        />
      </FieldWrapper>
    );
  }

  if (controlType === CONTROL.id) {
    const { id, button, counter, disabled, placeholder, readOnly } = attribute;
    const { disabled: _disabled, ...restField } = field;
    return (
      <FieldWrapper className={className} {...attribute}>
        <InputId
          button={button}
          counter={counter}
          disabled={disabled}
          id={id}
          isLoading={isLoading}
          placeholder={placeholder}
          readOnly={readOnly}
          {...restField}
        />
      </FieldWrapper>
    );
  }

  /** Default case */

  if (element?.richText) {
    return (
      <FieldWrapper className={className} {...attribute}>
        <EditorPlain
          button={attribute?.button}
          counter={attribute?.counter}
          disabled={attribute?.disabled}
          ext={ext}
          id={attribute?.id}
          isLoading={isLoading}
          type="input"
          value={field.value}
          onChange={field.onChange}
        />
      </FieldWrapper>
    );
  }

  const { disabled: _disabled, ...restField } = field;
  return (
    <FieldWrapper className={className} {...attribute}>
      <InputField
        {...restField}
        autoComplete="off"
        button={attribute?.button}
        counter={attribute?.counter}
        disabled={attribute?.disabled}
        id={attribute?.id}
        isLoading={isLoading}
        placeholder={attribute?.placeholder}
        sizesx={attribute?.size}
      />
    </FieldWrapper>
  );
}
